import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { emptyScene, formatInZone, sceneDocSchema } from '@system-atlas/shared';
import { ApiError, api, type DrawingDetail } from '../lib/api';
import { Alert, Spinner } from '../components/ui';
import { SceneEditor } from '../editor/SceneEditor';

export default function DrawingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [drawing, setDrawing] = useState<DrawingDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  // null = App-Config noch nicht geladen; im Zweifel (Fehler) altes Verhalten.
  const [collabEnabled, setCollabEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    if (!id) return;
    api
      .getDrawing(id)
      .then((r) => setDrawing(r.drawing))
      .catch((err) => setError(err instanceof ApiError ? err.message : t('errors.generic')));
  }, [id, t]);

  useEffect(() => {
    api
      .getAppConfig()
      .then((c) => setCollabEnabled(c.collabEnabled))
      .catch(() => setCollabEnabled(false));
  }, []);

  // null = Inhalt entspricht nicht dem Schema -> Editor NICHT oeffnen (sonst droht
  // Datenverlust: Autosave wuerde den echten Inhalt mit einer leeren Szene ueberschreiben).
  // Ausnahme: content = {} ist der DB-Default frisch angelegter Zeichnungen -> leere Szene.
  const initialScene = useMemo(() => {
    if (!drawing) return null;
    if (drawing.content !== null && typeof drawing.content === 'object' && Object.keys(drawing.content).length === 0) {
      return emptyScene;
    }
    const parsed = sceneDocSchema.safeParse(drawing.content);
    return parsed.success ? parsed.data : null;
  }, [drawing]);

  return (
    <div className="px-4 py-4">
      <div className="mb-3 flex items-center gap-3">
        <Link to="/" className="text-sm text-slate-500 hover:underline">
          ← {t('drawings.backToOverview')}
        </Link>
        {drawing && (
          <>
            <h1 className="text-lg font-semibold">{drawing.name}</h1>
            <span className="text-xs text-slate-400">
              {t('drawings.updatedAt', { date: formatInZone(drawing.updatedAt) })}
            </span>
          </>
        )}
      </div>

      {error && <Alert kind="error">{error}</Alert>}

      {(!drawing || collabEnabled === null) && !error ? (
        <Spinner />
      ) : drawing && initialScene && collabEnabled !== null ? (
        <SceneEditor
          drawingId={drawing.id}
          initialScene={initialScene}
          access={drawing.access}
          collabEnabled={collabEnabled}
        />
      ) : drawing ? (
        <Alert kind="error">{t('drawings.contentInvalid')}</Alert>
      ) : null}
    </div>
  );
}
